import {faCheck, faEdit, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import React from "react";

import {EDocumentTypes} from "@/components/project/models";
import {BTN_LINK_CLASSNAME} from "./models";

import NameChecker from "@toolbox/display-blocks/NameChecker";
import T from "@translate/T";
import ValidatedForm from "../button-like/ValidatedForm";

export interface IInlineRenameProps {
    id: string;
    type: EDocumentTypes;
    value: string;

    nameCheckId: number;
    nameCheckApi?: string; // endpoint URL, ID, type to perform name checking
    classNameDiv?: string;
    prefix?: string;
    hideButton?: boolean; // sets to true to do not show edit button
    readonly?: boolean;
    smallTitle?: boolean;
    h5Title?: boolean;

    onChanged(value: string): boolean | Promise<boolean>;
}

interface IInlineRenameState {
    text: string;
    isEditMode: boolean;
    hasSaveError: boolean;
}

class InlineRename extends React.PureComponent<
    IInlineRenameProps,
    IInlineRenameState
> {
    public readonly state: IInlineRenameState = {
        hasSaveError: false,
        isEditMode: false,
        text: "",
    };

    private get effective() {
        return this.state.text.trim();
    }

    @boundMethod
    public setInput(input: HTMLInputElement) {
        if (input) {
            input.focus();
        }
    }

    @boundMethod
    public showEditor(e: React.SyntheticEvent) {
        e.preventDefault();

        const {readonly, value} = this.props;
        if (readonly) {
            return;
        }

        this.setState({hasSaveError: false, isEditMode: true, text: value});
    }

    @boundMethod
    public cancel(e: React.SyntheticEvent) {
        e.preventDefault();

        this.setState({isEditMode: false});
    }

    @boundMethod
    public setText(e: React.ChangeEvent<HTMLInputElement>) {
        e.preventDefault();

        this.setState({text: e.target.value});
    }

    @boundMethod
    public detectCancel(e: React.KeyboardEvent) {
        if (e.key !== "Escape") {
            return;
        }

        e.preventDefault();

        this.setState({isEditMode: false});
    }

    @boundMethod
    public async saveChanges() {
        if (this.effective === "") {
            return;
        }

        try {
            const saved = await Promise.resolve(
                this.props.onChanged(this.effective),
            );
            const failed = saved === false;

            this.setState({hasSaveError: failed, isEditMode: failed});
        } catch {
            this.setState({hasSaveError: true});
        }
    }

    public render() {
        const {classNameDiv} = this.props;
        let className = "inline-rename";

        if (!!classNameDiv) {
            className += " " + classNameDiv;
        }

        return (
            <div className={className}>
                {this.renderValue()}
                {this.renderEditor()}
            </div>
        );
    }

    private renderValue() {
        if (this.state.isEditMode) {
            return null;
        }

        return (
            <div id="inline-value" className="d-flex align-items-end">
                {this.renderDisplayName()}
                {this.renderEditButton()}
            </div>
        );
    }

    @boundMethod
    private renderDisplayName() {
        const {id, prefix, value, smallTitle, h5Title} = this.props;
        const displayName = (prefix !== undefined ? `${prefix} ` : "") + value;
        const titleId = id + "-header";
        const className = "mb-0";

        if (smallTitle) {
            return (
                <span id={titleId} className={className}>
                    {displayName}
                </span>
            );
        }

        if (h5Title) {
            return (
                <h5 id={titleId} className={className}>
                    {displayName}
                </h5>
            );
        }

        return (
            <h3 id={titleId} className={className}>
                {displayName}
            </h3>
        );
    }

    private renderEditButton() {
        const {id, hideButton, readonly, value} = this.props;
        if (readonly || hideButton) {
            return null;
        }

        return (
            <button
                type="button"
                id={id + "-edit"}
                className={BTN_LINK_CLASSNAME + " ml-1"}
                aria-label={"open-rename-" + value}
                onClick={this.showEditor}
            >
                <FontAwesomeIcon icon={faEdit} fixedWidth={true} />
            </button>
        );
    }

    private renderEditor() {
        const {id, nameCheckId, nameCheckApi, type, value} = this.props;
        const {isEditMode, text} = this.state;
        if (!isEditMode) {
            return null;
        }

        return (
            <ValidatedForm suffixId="inline-rename" onSubmit={this.saveChanges}>
                <div className="input-group">
                    <input
                        ref={this.setInput}
                        type="text"
                        id={id + "-rename-input"}
                        className="form-control"
                        aria-label="rename"
                        required={true}
                        value={text}
                        onChange={this.setText}
                        onKeyDown={this.detectCancel}
                    />

                    <div className="input-group-append">
                        <button
                            type="submit"
                            id={id + "-check"}
                            className="btn btn-primary"
                            aria-label={"submit-rename-" + value}
                            disabled={!this.effective}
                        >
                            <FontAwesomeIcon icon={faCheck} fixedWidth={true} />
                        </button>
                        <button
                            type="button"
                            id={id + "-cancel"}
                            className="btn btn-secondary"
                            aria-label={"cancel-rename-" + value}
                            onClick={this.cancel}
                        >
                            <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                        </button>
                    </div>
                </div>

                {this.renderSaveError()}
                {nameCheckApi && (
                    <NameChecker
                        id={nameCheckId}
                        htmlNativ="small"
                        name={this.effective}
                        type={type}
                        apiUrl={nameCheckApi}
                    />
                )}
            </ValidatedForm>
        );
    }

    private renderSaveError() {
        if (!this.state.hasSaveError) {
            return null;
        }

        return (
            <div>
                <small id="save-error" className="text-danger">
                    <T>Error while saving, please try again!</T>
                </small>
            </div>
        );
    }
}

export default InlineRename;
